import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate } from '@/lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { HardDrive, Plus } from 'lucide-react';
import { SelectFilter, SearchInput, ClearFilters } from '@/components/filters';
import { buildQuery } from '@/lib/filters';
import { StatTiles } from '@/components/StatTiles';
import { PageHeader } from '@/components/PageHeader';
import { Pagination, DEFAULT_PAGE_SIZE } from '@/components/Pagination';
import { StatusBadge } from '@/components/StatusBadge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface DeviceSerial {
  id: string;
  serial: string;
  licenseId?: string | null;
  status: string;
  deviceInfo?: { model?: string; [key: string]: unknown } | null;
  claimedAt?: number | null;
  approvedAt?: number | null;
  activatedAt?: number | null;
  lastSeen?: number | null;
  notes?: string | null;
  licenseKey?: string | null;
  licenseName?: string | null;
  licenseStatus?: string | null;
  licenseFeatures?: {
    numCameras?: number;
    analytics?: string[];
  };
}

interface LicenseOption {
  id: string;
  key: string;
  name: string;
  activationStatus: string;
  customerName?: string;
}

const DEVICE_STATUSES = ['PREAPPROVED', 'CLAIM_PENDING', 'APPROVED', 'ACTIVATED', 'REVOKED'];

function Devices() {
  const { t } = useTranslation();
  const [devices, setDevices] = useState<DeviceSerial[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isRegisterDialogOpen, setIsRegisterDialogOpen] = useState(false);
  const [licenses, setLicenses] = useState<LicenseOption[]>([]);
  const [newDevice, setNewDevice] = useState({
    serial: '',
    licenseId: '',
    notes: '',
  });

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    const id = setTimeout(fetchDevices, search ? 300 : 0);
    return () => clearTimeout(id);
  }, [search, statusFilter, page]);

  // A filter change invalidates the current page — otherwise you can sit on
  // page 5 of a result set that now has one page and see an empty table.
  useEffect(() => { setPage(1); }, [search, statusFilter]);

  // Auto-refresh device statuses every 30s.
  useEffect(() => {
    const id = setInterval(fetchDevices, 30000);
    return () => clearInterval(id);
  }, [search, statusFilter]);

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('all');
  };

  const fetchDevices = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';
      const qs = buildQuery({
        limit: DEFAULT_PAGE_SIZE,
        page,
        search,
        status: statusFilter,
      });
      const response = await fetch(`${apiUrl}/api/device-serials${qs}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setDevices(data.data?.devices || []);
        setTotal(data.data?.total ?? 0);
      }
    } catch (error) {
      console.error('Failed to fetch devices:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchLicenses = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';
      const response = await fetch(`${apiUrl}/api/licenses?status=ACTIVE&limit=1000`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        const all: LicenseOption[] = data.data?.licenses || [];
        setLicenses(all.filter((l) => l.activationStatus === 'UNASSIGNED'));
      }
    } catch (error) {
      console.error('Failed to fetch licenses:', error);
    }
  };

  const openRegisterDialog = () => {
    setNewDevice({ serial: '', licenseId: '', notes: '' });
    fetchLicenses();
    setIsRegisterDialogOpen(true);
  };

  const handleRegisterDevice = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      const body: Record<string, string> = { serial: newDevice.serial.trim() };
      if (newDevice.licenseId) body.licenseId = newDevice.licenseId;
      if (newDevice.notes) body.notes = newDevice.notes;

      const response = await fetch(`${apiUrl}/api/device-serials`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      if (response.ok) {
        setIsRegisterDialogOpen(false);
        setNewDevice({ serial: '', licenseId: '', notes: '' });
        fetchDevices();
      }
    } catch (error) {
      console.error('Failed to register device serial:', error);
    }
  };

  const handleApproveDevice = async (device: DeviceSerial) => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      const response = await fetch(`${apiUrl}/api/device-serials/${device.id}/approve`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        fetchDevices();
      }
    } catch (error) {
      console.error('Failed to approve device:', error);
    }
  };

  const handleRevokeDevice = async (device: DeviceSerial) => {
    if (!window.confirm(`Revoke device serial ${device.serial}? The device will lose its license.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      const response = await fetch(`${apiUrl}/api/device-serials/${device.id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        fetchDevices();
      }
    } catch (error) {
      console.error('Failed to revoke device:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="w-full px-4 py-6 sm:px-6 lg:px-8">
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">{t('common.loading')}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6 px-4 py-6 sm:px-6 lg:px-8">
      <PageHeader
        eyebrow="License management"
        icon={HardDrive}
        title="Devices"
        subtitle="Hardware-serial device licensing for Jetson edge devices"
        actions={(
          <Button onClick={openRegisterDialog}>
            <Plus className="h-4 w-4 mr-2" />
            Register Serial
          </Button>
        )}
      />

      <StatTiles stats={[
        { label: t('devices.stats.total', { defaultValue: 'Matching devices' }), value: total || devices.length },
        { label: t('devices.stats.activated', { defaultValue: 'Activated on page' }), value: devices.filter((d) => d.status === 'ACTIVATED').length, color: 'text-success' },
        { label: t('devices.stats.approved', { defaultValue: 'Approved on page' }), value: devices.filter((d) => d.status === 'APPROVED').length, color: 'text-primary' },
        { label: t('devices.stats.pending', { defaultValue: 'Pending on page' }), value: devices.filter((d) => d.status === 'CLAIM_PENDING').length, color: 'text-amber-700 dark:text-amber-300' },
        { label: t('devices.stats.preapproved', { defaultValue: 'Preapproved on page' }), value: devices.filter((d) => d.status === 'PREAPPROVED').length },
        { label: t('devices.stats.revoked', { defaultValue: 'Revoked on page' }), value: devices.filter((d) => d.status === 'REVOKED').length, color: 'text-destructive' },
      ]} />

      <Card>
        <CardHeader>
          <CardTitle>All Devices</CardTitle>
          <CardDescription>
            {t('devices.matching', {
              defaultValue: '{{count}} matching device serials',
              count: total || devices.length,
            })}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="filter-bar">
            <SearchInput value={search} onChange={setSearch} placeholder="Serial or license key…" />
            <SelectFilter
              label="Status"
              value={statusFilter}
              onChange={setStatusFilter}
              options={DEVICE_STATUSES.map((v) => ({ value: v, label: v }))}
            />
            <ClearFilters onClick={clearFilters} />
          </div>
          {devices.length === 0 ? (
            <div className="empty-state">
              <p>{t('dashboard.noData')}</p>
            </div>
          ) : (
            <Table className="data-table stack-mobile rows-2line">
              <TableHeader>
                <TableRow>
                  <TableHead>{t('devices.table.serial', { defaultValue: 'Serial / claimed device' })}</TableHead>
                  <TableHead>License</TableHead>
                  <TableHead>Cameras</TableHead>
                  <TableHead>Analytics</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Last seen</TableHead>
                  <TableHead className="text-right">{t('users.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {devices.map((device) => {
                  const analytics = device.licenseFeatures?.analytics ?? [];
                  const licenseLabel = device.licenseName || device.licenseKey;
                  const meta = [licenseLabel, t('devices.seen', { defaultValue: 'Seen {{date}}', date: formatDate(device.lastSeen) })]
                    .filter(Boolean)
                    .join(' · ');
                  return (
                    <TableRow key={device.id}>
                      <TableCell className="cell-primary">
                        <div className="od-field">
                          <span className="line font-mono font-medium" title={device.serial}>{device.serial}</span>
                          {device.deviceInfo?.model && (
                            <span className="line line-sub text-xs text-muted-foreground" title={device.deviceInfo.model}>
                              {device.deviceInfo.model}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="cell-meta text-xs text-muted-foreground">{meta}</TableCell>
                      <TableCell data-label="License">
                        {licenseLabel ? (
                          <div className="od-field">
                            <span className="line font-medium" title={device.licenseName || ''}>{device.licenseName || '-'}</span>
                            {device.licenseKey && (
                              <span className="line line-sub text-xs text-muted-foreground font-mono" title={device.licenseKey}>
                                {device.licenseKey}
                              </span>
                            )}
                          </div>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell data-label="Cameras">{device.licenseFeatures?.numCameras ?? '—'}</TableCell>
                      <TableCell data-label="Analytics">
                        {analytics.length ? (
                          <div className="flex flex-nowrap gap-1" title={analytics.join(', ')}>
                            {analytics.slice(0, 2).map((a) => (
                              <Badge key={a} variant="secondary">
                                {a}
                              </Badge>
                            ))}
                            {analytics.length > 2 && <Badge variant="outline">+{analytics.length - 2}</Badge>}
                          </div>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell className="cell-status">
                        <StatusBadge status={device.status} />
                      </TableCell>
                      <TableCell data-label="Last seen">{formatDate(device.lastSeen)}</TableCell>
                      <TableCell className="cell-actions text-right">
                        {(device.status === 'CLAIM_PENDING' || device.status === 'PREAPPROVED') && (
                          <Button variant="ghost" size="sm" onClick={() => handleApproveDevice(device)}>
                            Approve
                          </Button>
                        )}
                        {device.status !== 'REVOKED' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-destructive hover:text-destructive"
                            onClick={() => handleRevokeDevice(device)}
                          >
                            Revoke
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
          <Pagination page={page} pageSize={DEFAULT_PAGE_SIZE} total={total} onPageChange={setPage} label="devices" />
        </CardContent>
      </Card>

      {/* Register Serial Dialog */}
      <Dialog open={isRegisterDialogOpen} onOpenChange={setIsRegisterDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Register Serial</DialogTitle>
            <DialogDescription>
              Pre-approve a device hardware serial. Optionally assign a license now.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="serial">Serial</Label>
              <Input
                id="serial"
                value={newDevice.serial}
                onChange={(e) => setNewDevice({ ...newDevice, serial: e.target.value })}
                placeholder="e.g. 1423919012345"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="license">License ({t('common.optional')})</Label>
              <Select
                value={newDevice.licenseId || 'none'}
                onValueChange={(value) =>
                  setNewDevice({ ...newDevice, licenseId: value === 'none' ? '' : value })
                }
              >
                <SelectTrigger id="license">
                  <SelectValue placeholder="Assign later" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Assign later</SelectItem>
                  {licenses.map((license) => (
                    <SelectItem key={license.id} value={license.id}>
                      {`${license.name} (${license.key})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="notes">Notes ({t('common.optional')})</Label>
              <Input
                id="notes"
                value={newDevice.notes}
                onChange={(e) => setNewDevice({ ...newDevice, notes: e.target.value })}
                placeholder="e.g. Site A, camera gateway"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsRegisterDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleRegisterDevice} disabled={!newDevice.serial.trim()}>
              Register
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default Devices;
